 <template>
  <div class="simulador-fiscal-wrapper">
    <!-- ============================================================ -->
    <!-- CABEÇALHO                                                     -->
    <!-- ============================================================ -->
    <header class="simulador-header">
      <div class="titulo-wrapper">
        <h4 class="titulo-principal">
          <i class="bi bi-cpu me-2"></i> Simulador do Motor Fiscal
        </h4>
        <span class="titulo-sub">Teste a resolução da Matriz Fiscal e o cálculo de impostos</span>
      </div>
      <div class="botoes-wrapper">
        <button type="button" class="btn btn-outline-light btn-sm" @click="reiniciarSimulacao">
          <i class="bi bi-arrow-counterclockwise me-1"></i> Reiniciar Simulação
        </button>
      </div>
    </header>

    <!-- ============================================================ -->
    <!-- CORPO                                                         -->
    <!-- ============================================================ -->
    <div class="simulador-body">

      <!-- ============================================================ -->
      <!-- ROW 1: FORMULÁRIO + DIAGNÓSTICO                             -->
      <!-- ============================================================ -->
      <div class="row g-4">

        <!-- COLUNA ESQUERDA: FORMULÁRIO -->
        <div class="col-lg-5">
          <div class="card border-primary-subtle shadow-sm">
            <div class="card-header bg-primary-subtle py-2">
              <h6 class="mb-0 text-primary-emphasis fw-bold">
                <i class="bi bi-sliders me-2"></i> Parâmetros da Simulação
              </h6>
            </div>
            <div class="card-body">
              <!-- Linha 1: Nível Fiscal + Tipo Movimento + Valor Base -->
              <div class="row g-2 mb-2">
                <div class="col-md-4">
                  <CampoSelect id="lstNivelModuloFiscal" label="Nível Fiscal"
                    v-model="formData.lstNivelModuloFiscal" :options="nivelFiscalOptions"
                    option-label="textoLista" option-value="idListaClienteItem"
                    :disabled="!isAdminInterno" required />
                </div>
                <div class="col-md-4">
                  <CampoSelect id="lstTipoMovimento" label="Tipo Movimento"
                    v-model="formData.lstTipoMovimento" :options="tipoMovimentoOptions"
                    option-label="textoLista" option-value="idListaClienteItem" required />
                </div>
                <div class="col-md-4">
                  <CampoInput id="valorBase" label="Valor Negociado R$" v-model="formData.valorBase" type="moeda" required />
                </div>
                <div class="col-md-4">
                  <CampoInput id="valorDesconto" label="Valor Desconto R$" v-model="formData.valorDesconto" type="moeda" />
                </div>
              </div>

              <!-- Linha 2: Filial + Operação -->
              <div class="row g-2 mb-2">
                <div class="col-md-6">
                  <CampoBusca id="idFilial" label="Filial" v-model="formData.idFilial"
                    :configuracaoBusca="filialSearch" @selecionado="handleFilialSelecionado" required />
                </div>
                <div class="col-md-6">
                  <CampoBusca id="idOperacao" label="Operação" v-model="formData.idOperacao"
                    :configuracaoBusca="configBuscaOperacao" @selecionado="handleOperacaoSelecionado" required />
                </div>
              </div>
              <div v-if="formData.idOperacao" class="mb-2">
                <small class="text-muted">Operação:</small>
                <span class="fw-bold ms-1">{{ operacaoDescricao }}</span>
                <span class="badge bg-secondary ms-1">{{ operacaoTipoMovimento }}</span>
              </div>

              <!-- Linha 3: Parceiro + Produto -->
              <div class="row g-2 mb-2">
                <div class="col-md-6">
                  <CampoBusca id="idParceiro" label="Parceiro" v-model="formData.idParceiro"
                    :configuracaoBusca="configBuscaParceiro" @selecionado="handleParceiroSelecionado" required />
                  <div v-if="parceiroInfo" class="d-flex flex-wrap gap-2 mt-1">
                    <span class="badge bg-light text-dark border" style="font-size:0.7rem;">
                      <span class="text-muted">Tipo:</span> {{ parceiroInfo.tipoPessoa }}
                    </span>
                    <span class="badge bg-light text-dark border" style="font-size:0.7rem;">
                      <span class="text-muted">UF:</span> {{ parceiroInfo.ufDestino }}
                    </span>
                    <span class="badge bg-light text-dark border" style="font-size:0.7rem;" v-if="parceiroInfo.regimeTributario">
                      <span class="text-muted">Regime:</span> {{ parceiroInfo.regimeTributario }}
                    </span>
                    <span class="badge bg-light text-dark border" style="font-size:0.7rem;" v-if="parceiroInfo.isOrgaoGoverno">
                      <span class="text-muted">Órgão:</span> Governo
                    </span>
                  </div>
                </div>
                <div class="col-md-6">
                  <CampoBusca id="idProduto" label="Produto / Serviço" v-model="formData.idProduto"
                    :configuracaoBusca="configBuscaProduto" @selecionado="handleProdutoSelecionado" required />
                  <div v-if="produtoInfo" class="d-flex flex-wrap gap-2 mt-1">
                    <span class="badge bg-light text-dark border" style="font-size:0.7rem;">
                      <span class="text-muted">Código:</span> {{ produtoInfo.codigoFiscal }}
                    </span>
                    <span class="badge bg-light text-dark border" style="font-size:0.7rem;">
                      <span class="text-muted">Natureza:</span> {{ produtoInfo.naturezaLabel }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- ROTA + BOTÕES -->
              <div v-if="formData.idFilial && formData.idParceiro" class="bg-success-subtle border border-success-subtle rounded p-2 mt-2">
                <div class="row g-1 align-items-center">
                  <div class="col text-center">
                    <div class="small text-muted">{{ nomeOrigemResumo }}</div>
                    <div class="fw-bold fs-6">{{ ufOrigemResumo }}</div>
                  </div>
                  <div class="col-auto"><i class="bi bi-arrow-right fs-4 text-success"></i></div>
                  <div class="col text-center">
                    <div class="small text-muted">{{ nomeDestinoResumo }}</div>
                    <div class="fw-bold fs-6">{{ ufDestinoResumo }}</div>
                  </div>
                  <div class="col-auto">
                    <span class="badge bg-success fs-6">
                      <i :class="isEntrada ? 'bi-box-arrow-in-down' : 'bi-box-arrow-up'" class="me-1"></i>
                      {{ isEntrada ? 'Entrada' : 'Saída' }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2 mt-3">
                <button type="button" class="btn btn-primary flex-fill fw-bold" @click="handleSimular"
                  :disabled="!podeSimular || loading || diagnosticando">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="bi bi-play-fill me-2"></i>
                  {{ loading ? 'Calculando...' : 'Simular' }}
                </button>
                <button type="button" class="btn btn-outline-info flex-fill fw-bold" @click="handleDiagnosticarRegras"
                  :disabled="!podeSimular || loading || diagnosticando">
                  <span v-if="diagnosticando" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="bi bi-search-heart me-2"></i>
                  {{ diagnosticando ? 'Diagnosticando...' : 'Diagnosticar' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- COLUNA DIREITA: DIAGNÓSTICO DE REGRAS -->
        <div class="col-lg-7">
          <!-- Estado vazio (sem resultado nem diagnóstico) -->
          <div v-if="!resultado && !erro && !diagnostico" class="card border-secondary-subtle shadow-sm">
            <div class="card-body text-center py-5">
              <i class="bi bi-calculator fs-1 text-muted d-block mb-3"></i>
              <p class="text-muted mb-0">
                Preencha os campos ao lado e clique em <strong>Simular</strong> ou <strong>Diagnosticar</strong>.
              </p>
            </div>
          </div>

          <!-- DIAGNÓSTICO DE PARÂMETROS (prontidão dos cadastros) -->
          <div v-if="diagnostico">
            <div class="card mb-3" :class="diagnostico.pronto ? 'border-success' : 'border-danger'">
              <div class="card-body d-flex align-items-center justify-content-between py-2">
                <div>
                  <h6 class="mb-0" :class="diagnostico.pronto ? 'text-success' : 'text-danger'">
                    <i :class="diagnostico.pronto ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'" class="me-2"></i>
                    {{ diagnostico.pronto ? 'Parâmetros prontos para simular' : 'Há pendências nos cadastros' }}
                  </h6>
                  <small class="text-muted">{{ diagnostico.mensagem }}</small>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="limparDiagnostico">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <!-- Resumo compacto do checklist -->
            <div class="row g-1 mb-2">
              <div class="col-3"><div class="card border-secondary-subtle text-center py-1"><small class="text-muted">Itens</small><strong>{{ diagnostico.resumo?.totalItens || 0 }}</strong></div></div>
              <div class="col-3"><div class="card border-success-subtle text-center py-1"><small class="text-muted">OK</small><strong class="text-success">{{ diagnostico.resumo?.totalOk || 0 }}</strong></div></div>
              <div class="col-3"><div class="card border-warning-subtle text-center py-1"><small class="text-muted">Atenção</small><strong class="text-warning">{{ diagnostico.resumo?.totalAtencoes || 0 }}</strong></div></div>
              <div class="col-3"><div class="card border-danger-subtle text-center py-1"><small class="text-muted">Pendências</small><strong class="text-danger">{{ diagnostico.resumo?.totalFaltas || 0 }}</strong></div></div>
            </div>

            <!-- Regra da Matriz aplicável (resumo — a árvore de etapas fica no JSON da simulação) -->
            <div v-if="diagnostico.regraAplicavel" class="card mb-2" :class="diagnostico.regraAplicavel.encontrada ? 'border-success-subtle' : 'border-danger-subtle'">
              <div class="card-body py-1 small d-flex justify-content-between align-items-center gap-2">
                <span>
                  <i :class="diagnostico.regraAplicavel.encontrada ? 'bi bi-check-circle-fill text-success' : 'bi bi-x-circle-fill text-danger'" class="me-1"></i>
                  <strong>Matriz Fiscal:</strong>
                  <span class="ms-1">{{ diagnostico.regraAplicavel.mensagem }}</span>
                </span>
                <span v-if="diagnostico.regraAplicavel.perfilDescricao" class="badge bg-secondary" style="font-size:0.6rem;">{{ diagnostico.regraAplicavel.perfilDescricao }}</span>
              </div>
            </div>

            <!-- Categorias do checklist de parâmetros (accordions) -->
            <div v-for="(cat, ci) in diagnostico.categorias" :key="ci"
              class="card mb-2" :class="classeBordaCategoria(cat.status)">
              <div class="card-header py-1 cursor-pointer d-flex align-items-center"
                @click="toggleCategoria(ci)">
                <small class="fw-bold">
                  <i class="bi bi-folder2-open me-1"></i>{{ cat.nome }}
                </small>
                <span class="badge ms-2" :class="classeBadgeStatus(cat.status)" style="font-size:0.6rem;">{{ labelStatus(cat.status) }}</span>
                <i :class="categoriaAberta(ci) ? 'bi-chevron-up' : 'bi-chevron-down'" class="ms-auto"></i>
              </div>
              <div v-show="categoriaAberta(ci)" class="card-body p-2">
                <div v-for="(item, ii) in cat.itens" :key="ii" class="border-bottom pb-1 mb-1">
                  <div class="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <span class="fw-bold small">{{ item.titulo }}</span>
                      <span v-if="item.detalhe" class="d-block text-muted" style="font-size:0.7rem;">
                        {{ item.detalhe }}
                      </span>
                      <span v-if="item.acao" class="d-block text-danger" style="font-size:0.7rem;">
                        <i class="bi bi-tools me-1"></i>{{ item.acao }}
                      </span>
                    </div>
                    <span class="badge" :class="classeBadgeStatus(item.status)" style="font-size:0.6rem;">{{ labelStatus(item.status) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- JSON Bruto (toggle compacto) -->
            <div class="card border-secondary">
              <div class="card-header bg-secondary text-white py-1 cursor-pointer d-flex align-items-center"
                @click="exibirJsonDiagnostico = !exibirJsonDiagnostico">
                <small><i class="bi bi-code-slash me-1"></i>JSON Bruto</small>
                <div class="ms-auto d-flex align-items-center gap-2">
                  <button type="button" class="btn btn-sm btn-outline-light" @click.stop="copiarDiagnostico"
                    :title="copiadoDiagnostico ? 'Copiado!' : 'Copiar JSON'">
                    <i :class="copiadoDiagnostico ? 'bi bi-check-lg' : 'bi bi-clipboard'" class="me-1"></i>
                    {{ copiadoDiagnostico ? 'Copiado!' : 'Copiar' }}
                  </button>
                  <i :class="exibirJsonDiagnostico ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </div>
              </div>
              <div v-show="exibirJsonDiagnostico" class="card-body p-0">
                <pre class="json-pre mb-0" style="max-height:200px;"><code>{{ JSON.stringify(diagnostico, null, 2) }}</code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- ROW 2: RESULTADO DA SIMULAÇÃO (full width, abaixo)          -->
      <!-- ============================================================ -->
      <div v-if="resultado" class="row g-4 mt-3">
        <div class="col-12">
          <div class="card" :class="resultado.sucesso ? 'border-success' : 'border-danger'">
            <div class="card-body d-flex align-items-center justify-content-between py-2">
              <div>
                <h5 class="mb-0" :class="resultado.sucesso ? 'text-success' : 'text-danger'">
                  <i :class="resultado.sucesso ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'" class="me-2"></i>
                  {{ resultado.sucesso ? 'Cálculo realizado com sucesso' : 'Falha no cálculo' }}
                </h5>
                <small class="text-muted">{{ resultado.mensagemDebug }}</small>
              </div>
              <div class="d-flex align-items-center gap-2">
                <span class="badge bg-secondary fs-6">{{ resultado.nomePerfilAplicado }}</span>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="resultado = null">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          </div>

          <template v-if="resultado.sucesso">
            <div class="row g-2 mt-2">
              <!-- ICMS -->
              <div class="col-md-6 col-lg-4">
                <div class="card h-100 border-info-subtle">
                  <div class="card-header bg-info-subtle py-1"><small class="fw-bold text-info-emphasis"><i class="bi bi-building me-1"></i>ICMS</small></div>
                  <div class="card-body py-1 small">
                    <div class="d-flex justify-content-between"><span class="text-muted">Alíquota:</span><strong>{{ resultado.aliquotaIcms }}%</strong></div>
                    <div class="d-flex justify-content-between"><span class="text-muted">Valor:</span><strong>{{ formatarMoeda(resultado.valorIcms) }}</strong></div>
                    <div class="d-flex justify-content-between"><span class="text-muted">Efetiva:</span><strong>{{ resultado.aliquotaIcmsEfetiva }}%</strong></div>
                    <div class="d-flex justify-content-between"><span class="text-muted">Vlr Efetivo:</span><strong>{{ formatarMoeda(resultado.valorIcmsEfetivo) }}</strong></div>
                    <div class="d-flex justify-content-between" v-if="resultado.cstIcmsCodigo"><span class="text-muted">CST:</span><strong>{{ resultado.cstIcmsCodigo }}</strong></div>
                    <div class="d-flex justify-content-between" v-if="resultado.csosnCodigo"><span class="text-muted">CSOSN:</span><strong>{{ resultado.csosnCodigo }}</strong></div>
                  </div>
                </div>
              </div>
              <!-- PIS -->
              <div class="col-md-6 col-lg-4">
                <div class="card h-100 border-info-subtle">
                  <div class="card-header bg-info-subtle py-1"><small class="fw-bold text-info-emphasis"><i class="bi bi-receipt me-1"></i>PIS</small></div>
                  <div class="card-body py-1 small">
                    <div class="d-flex justify-content-between"><span class="text-muted">Alíquota:</span><strong>{{ resultado.aliquotaPis }}%</strong></div>
                    <div class="d-flex justify-content-between"><span class="text-muted">Valor:</span><strong>{{ formatarMoeda(resultado.valorPis) }}</strong></div>
                    <div class="d-flex justify-content-between" v-if="resultado.cstPisCodigo"><span class="text-muted">CST:</span><strong>{{ resultado.cstPisCodigo }}</strong></div>
                  </div>
                </div>
              </div>
              <!-- COFINS -->
              <div class="col-md-6 col-lg-4">
                <div class="card h-100 border-info-subtle">
                  <div class="card-header bg-info-subtle py-1"><small class="fw-bold text-info-emphasis"><i class="bi bi-receipt me-1"></i>COFINS</small></div>
                  <div class="card-body py-1 small">
                    <div class="d-flex justify-content-between"><span class="text-muted">Alíquota:</span><strong>{{ resultado.aliquotaCofins }}%</strong></div>
                    <div class="d-flex justify-content-between"><span class="text-muted">Valor:</span><strong>{{ formatarMoeda(resultado.valorCofins) }}</strong></div>
                    <div class="d-flex justify-content-between" v-if="resultado.cstCofinsCodigo"><span class="text-muted">CST:</span><strong>{{ resultado.cstCofinsCodigo }}</strong></div>
                  </div>
                </div>
              </div>
              <!-- IPI -->
              <div class="col-md-6 col-lg-4">
                <div class="card h-100 border-info-subtle">
                  <div class="card-header bg-info-subtle py-1"><small class="fw-bold text-info-emphasis"><i class="bi bi-box me-1"></i>IPI</small></div>
                  <div class="card-body py-1 small">
                    <div class="d-flex justify-content-between"><span class="text-muted">Alíquota:</span><strong>{{ resultado.aliquotaIpi }}%</strong></div>
                    <div class="d-flex justify-content-between"><span class="text-muted">Valor:</span><strong>{{ formatarMoeda(resultado.valorIpi) }}</strong></div>
                    <div class="d-flex justify-content-between" v-if="resultado.cstIpiCodigo"><span class="text-muted">CST:</span><strong>{{ resultado.cstIpiCodigo }}</strong></div>
                  </div>
                </div>
              </div>
              <!-- ADUANEIROS -->
              <div class="col-md-6 col-lg-4" v-if="resultado.valorIi > 0 || resultado.valorIe > 0">
                <div class="card h-100 border-warning-subtle">
                  <div class="card-header bg-warning-subtle py-1"><small class="fw-bold text-warning-emphasis"><i class="bi bi-globe me-1"></i>Aduaneiros</small></div>
                  <div class="card-body py-1 small">
                    <div class="d-flex justify-content-between"><span class="text-muted">II:</span><strong>{{ resultado.aliquotaIi }}% / {{ formatarMoeda(resultado.valorIi) }}</strong></div>
                    <div class="d-flex justify-content-between"><span class="text-muted">IE:</span><strong>{{ resultado.aliquotaIe }}% / {{ formatarMoeda(resultado.valorIe) }}</strong></div>
                  </div>
                </div>
              </div>
              <!-- DIFAL -->
              <div class="col-md-6 col-lg-4" v-if="resultado.valorDifal > 0">
                <div class="card h-100 border-warning-subtle">
                  <div class="card-header bg-warning-subtle py-1"><small class="fw-bold text-warning-emphasis"><i class="bi bi-arrow-left-right me-1"></i>DIFAL</small></div>
                  <div class="card-body py-1 small">
                    <div class="d-flex justify-content-between"><span class="text-muted">Alíquota:</span><strong>{{ resultado.aliquotaDifal }}%</strong></div>
                    <div class="d-flex justify-content-between"><span class="text-muted">Valor:</span><strong>{{ formatarMoeda(resultado.valorDifal) }}</strong></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- RETENÇÕES -->
            <div class="card border-secondary-subtle mt-2" v-if="resultado.valorTotalRetencoes > 0">
              <div class="card-header bg-secondary-subtle py-1"><small class="fw-bold"><i class="bi bi-percent me-1"></i>Retenções</small></div>
              <div class="card-body py-1 small">
                <div class="row g-1">
                  <div class="col-md-3 col-6" v-if="resultado.valorIrrf > 0"><span class="text-muted">IRRF:</span> <strong>{{ formatarMoeda(resultado.valorIrrf) }}</strong></div>
                  <div class="col-md-3 col-6" v-if="resultado.valorInss > 0"><span class="text-muted">INSS:</span> <strong>{{ formatarMoeda(resultado.valorInss) }}</strong></div>
                  <div class="col-md-3 col-6" v-if="resultado.valorCsll > 0"><span class="text-muted">CSLL:</span> <strong>{{ formatarMoeda(resultado.valorCsll) }}</strong></div>
                  <div class="col-md-3 col-6" v-if="resultado.valorPisRetido > 0"><span class="text-muted">PIS Ret.:</span> <strong>{{ formatarMoeda(resultado.valorPisRetido) }}</strong></div>
                  <div class="col-md-3 col-6" v-if="resultado.valorCofinsRetida > 0"><span class="text-muted">COFINS Ret.:</span> <strong>{{ formatarMoeda(resultado.valorCofinsRetida) }}</strong></div>
                  <div class="col-md-3 col-6" v-if="resultado.valorIss > 0"><span class="text-muted">ISS:</span> <strong>{{ formatarMoeda(resultado.valorIss) }}</strong></div>
                </div>
                <hr class="my-1" />
                <div class="d-flex justify-content-between fw-bold"><span>Total Retenções:</span><span>{{ formatarMoeda(resultado.valorTotalRetencoes) }}</span></div>
              </div>
            </div>

            <!-- ENGENHARIA FINANCEIRA -->
            <div class="card border-success-subtle mt-2">
              <div class="card-header bg-success-subtle py-1"><small class="fw-bold text-success-emphasis"><i class="bi bi-graph-up me-1"></i>Engenharia Financeira</small></div>
              <div class="card-body py-1 small">
                <div class="row g-1">
                  <div class="col-3"><span class="text-muted">Fator:</span> <strong>{{ resultado.fatorImpostosAplicado }}</strong></div>
                  <div class="col-3"><span class="text-muted">Incentivo:</span> <strong>{{ resultado.ganhoIncentivoRegional }}%</strong></div>
                  <div class="col-3"><span class="text-muted">Total c/ Imp.:</span> <strong>{{ formatarMoeda(resultado.valorTotalComImpostos) }}</strong></div>
                  <div class="col-3"><span class="text-muted">Líquido:</span> <strong>{{ formatarMoeda(resultado.valorLiquidoAPagar) }}</strong></div>
                </div>
              </div>
            </div>

            <!-- TÍTULOS SIMULADOS (visão hierárquica por obrigação) -->
            <div class="mt-2" v-if="resultado.titulosSimulados?.length">
              <div class="card border-primary-subtle">
                <div class="card-header bg-primary-subtle py-1">
                  <small class="fw-bold text-primary-emphasis">
                    <i class="bi bi-diagram-3 me-1"></i>Títulos Financeiros Simulados
                  </small>
                </div>
              </div>
              <div v-for="(titulo, ti) in resultado.titulosSimulados" :key="ti" class="card border-secondary-subtle mt-1">
                <div class="card-header bg-secondary-subtle py-1 d-flex align-items-center justify-content-between">
                  <div>
                    <span class="badge" :class="badgeEsfera(titulo.esfera)" style="font-size:0.6rem;">{{ titulo.esfera }}</span>
                    <span class="fw-bold ms-1">{{ titulo.tipoTributo }}</span>
                    <small class="text-muted ms-2">{{ titulo.favorecido }}</small>
                  </div>
                  <div>
                    <span class="fw-bold">{{ formatarMoeda(titulo.valorTitulo) }}</span>
                    <span v-if="titulo.idParceiroCredor" class="badge bg-info text-dark ms-1" style="font-size:0.6rem;">
                      ID {{ titulo.idParceiroCredor }}
                    </span>
                  </div>
                </div>
                <div class="card-body p-0 small">
                  <table class="table table-sm mb-0 small">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Categoria</th>
                        <th>Tipo</th>
                        <th class="text-end">Valor</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(cat, ci) in titulo.categorias" :key="ci">
                        <td>
                          <span v-if="cat.codigoEstruturado" class="badge bg-light text-dark border" style="font-size:0.6rem;">
                            {{ cat.codigoEstruturado }}
                          </span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td>
                          <span v-if="cat.nome" class="fw-bold">{{ cat.nome }}</span>
                          <span v-else class="text-muted">(não configurada)</span>
                        </td>
                        <td>
                          <span class="badge" :class="classeBadgeTipoExibicao(cat.tipoExibicao)" style="font-size:0.6rem;">
                            {{ labelTipoExibicao(cat.tipoExibicao, resultado.resumoFinanceiro?.isCompra) }}
                          </span>
                        </td>
                        <td class="text-end fw-bold">{{ formatarMoeda(cat.valorFatia) }}</td>
                        <td>
                          <span v-if="cat.observacao" class="text-warning" :title="cat.observacao" style="cursor:help;">
                            <i class="bi bi-exclamation-triangle-fill"></i>
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="titulo.categorias?.length" class="card-footer bg-light py-1 small d-flex justify-content-between">
                  <span class="text-muted">Soma das categorias:</span>
                  <span class="fw-bold">{{ formatarMoeda(titulo.categorias.reduce((acc, c) => acc + (c.valorFatia || 0), 0)) }}</span>
                </div>
              </div>
              <div v-if="resultado.resumoFinanceiro" class="card border-primary-subtle mt-1">
                <div class="card-footer bg-primary-subtle py-1 small d-flex justify-content-between">
                  <span class="fw-bold">{{ resultado.resumoFinanceiro.descricaoLinha }}:</span>
                  <span>
                    {{ formatarMoeda(resultado.resumoFinanceiro.valorBruto) }}
                    <span v-if="resultado.resumoFinanceiro.valorDesconto > 0" class="text-danger ms-1">
                      − {{ formatarMoeda(resultado.resumoFinanceiro.valorDesconto) }}
                    </span>
                    <span class="ms-1 fw-bold">= {{ formatarMoeda(resultado.resumoFinanceiro.valorLiquido) }}</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- JSON BRUTO -->
            <div class="card border-secondary mt-2">
              <div class="card-header bg-secondary text-white py-1 cursor-pointer d-flex align-items-center" @click="exibirJson = !exibirJson">
                <small><i class="bi bi-code-slash me-1"></i>JSON Bruto</small>
                <div class="ms-auto d-flex align-items-center gap-2">
                  <button type="button" class="btn btn-sm btn-outline-light" @click.stop="copiarResultado"
                    :title="copiadoResultado ? 'Copiado!' : 'Copiar JSON'">
                    <i :class="copiadoResultado ? 'bi bi-check-lg' : 'bi bi-clipboard'" class="me-1"></i>
                    {{ copiadoResultado ? 'Copiado!' : 'Copiar' }}
                  </button>
                  <i :class="exibirJson ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </div>
              </div>
              <div v-show="exibirJson" class="card-body p-0">
                <pre class="json-pre mb-0" style="max-height:300px;"><code>{{ JSON.stringify(resultado, null, 2) }}</code></pre>
              </div>
            </div>

            <!-- MEMÓRIA DE CÁLCULO (transcript para IA) -->
            <div class="card border-dark mt-2" v-if="resultado.sucesso && resultado.memoriaCalculoJson">
              <div class="card-header bg-dark text-white py-1 d-flex align-items-center justify-content-between">
                <small class="fw-bold"><i class="bi bi-journal-text me-1"></i>Memória de Cálculo (Transcrito para IA)</small>
                <button type="button" class="btn btn-sm btn-outline-light" @click="copiarMemoriaCalculo"
                  :title="copiado ? 'Copiado!' : 'Copiar JSON'">
                  <i :class="copiado ? 'bi bi-check-lg' : 'bi bi-clipboard'" class="me-1"></i>
                  {{ copiado ? 'Copiado!' : 'Copiar' }}
                </button>
              </div>
              <div class="card-body p-0">
                <textarea class="form-control border-0 rounded-0 font-monospace small" readonly
                  style="min-height:200px; max-height:400px; resize:vertical; background:#1e1e1e; color:#d4d4d4;"
                  :value="resultado.memoriaCalculoJson"></textarea>
              </div>
            </div>
          </template>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaOperacao } from '@/search-definitions/operacaoSearch'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { GEO_UF } from '@/constants/GEO_UF'
import { TP_MOVIMENTO } from '@/constants/TP_MOVIMENTO'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'
import { PROD_NATUREZA } from '@/constants/PROD_NATUREZA'

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

// ============================================================
// LISTAS CARREGADAS VIA DICIONÁRIO DE METADADOS
// ============================================================
const listasLookup = reactive({})

const carregarListas = async () => {
  // Carrega listas de Parceiro (TipoPessoa, RegimeTributario) + Produto (NBS, LC116)
  const result = await metadataStore.carregarListasParaTabelas(['Parceiro', 'Produto'])
  Object.assign(listasLookup, result)
}

// ============================================================
// NÍVEL FISCAL: carregado via lookup da API
// ============================================================
const nivelFiscalOptions = ref([])
const nivelFiscalCarregado = ref(false)

const carregarNivelFiscal = async () => {
  try {
    const res = await api.get('/ListasSistema/lookup/NV_MODULO_FISCAL')
    const itens = res.data || []
    nivelFiscalOptions.value = itens.map(item => ({
      idListaClienteItem: item.idListaSistemaItem,
      textoLista: item.textoLista
    }))
    nivelFiscalCarregado.value = true

    // Pré-seleciona o nível fiscal da empresa do usuário logado
    const nivelDaEmpresa = authStore.lstNivelModuloFiscal
    if (nivelDaEmpresa && nivelDaEmpresa !== NV_MODULO_FISCAL.Nenhum) {
      const encontrado = itens.some(i =>
        String(i.idListaSistemaItem).toUpperCase() === String(nivelDaEmpresa).toUpperCase()
      )
      if (encontrado) {
        formData.lstNivelModuloFiscal = nivelDaEmpresa
      }
    }
  } catch (e) {
    console.warn('[SimuladorFiscal] Erro ao carregar lookup NV_MODULO_FISCAL, usando fallback:', e)
    nivelFiscalOptions.value = [
      { idListaClienteItem: NV_MODULO_FISCAL.Essential, textoLista: 'Essential (Manual)' },
      { idListaClienteItem: NV_MODULO_FISCAL.Advanced, textoLista: 'Advanced (Automático)' },
      { idListaClienteItem: NV_MODULO_FISCAL.Compliance, textoLista: 'Compliance (Automático + Validações)' }
    ]
    nivelFiscalCarregado.value = true
  }
}

onMounted(async () => {
  await carregarListas()
  carregarNivelFiscal()
})

// Utilitário para resolver texto de lista por código
const getTextoLista = (listaKey, codigo) => {
  if (!codigo) return null
  const lista = listasLookup[listaKey] || []
  const item = lista.find(i => String(i.idListaClienteItem) === String(codigo))
  return item ? item.textoLista : codigo
}

const isAdminInterno = computed(() => authStore.isAdminInterno)

// ============================================================
// UTILITÁRIOS DE UF (normalização sigla ↔ código interno)
// ============================================================
// Reverse-map: código interno → sigla (ex: "GEO_UF8" → "ES")
const UF_PARA_SIGLA = Object.fromEntries(
  Object.entries(GEO_UF).map(([sigla, codigo]) => [codigo, sigla])
)

// Converte qualquer formato de UF para sigla (exibição)
// Aceita: "ES" (mantém), "GEO_UF8" (converte para "ES")
const ufParaSigla = (uf) => {
  if (!uf) return null
  const upper = uf.toUpperCase()
  if (upper.length === 2) return upper
  return UF_PARA_SIGLA[upper] || upper
}

// Converte qualquer formato de UF para código interno (envio API)
// Aceita: "ES" (converte para "GEO_UF8"), "GEO_UF8" (mantém)
const ufParaCodigo = (uf) => {
  if (!uf) return null
  const upper = uf.toUpperCase()
  if (upper.length === 2) return GEO_UF[upper] || upper
  return upper
}

// ============================================================
// ESTADO DO FORMULÁRIO
// ============================================================
const formData = reactive({
  idFilial: null,
  idOperacao: null,
  idParceiro: null,
  idProduto: null,
  valorBase: null,
  valorDesconto: null,
  lstTipoMovimento: TP_MOVIMENTO.Entrada,
  lstNivelModuloFiscal: null
})

const filialSelecionada = ref(null)
const operacaoSelecionada = ref(null)
const parceiroSelecionado = ref(null)
const produtoSelecionado = ref(null)

const loading = ref(false)
const resultado = ref(null)
const erro = ref(null)
const exibirJson = ref(false)
const exibirRelatorioVerificacoes = ref(false)
const relatorioVerificacoes = ref(null)

// Diagnóstico de parâmetros (prontidão dos cadastros por categoria)
const diagnosticando = ref(false)
const diagnostico = ref(null)
const exibirJsonDiagnostico = ref(false)
const categoriasAbertas = ref({})
const copiado = ref(false)
const copiadoDiagnostico = ref(false)
const copiadoResultado = ref(false)

// ============================================================
// HELPERS DE STATUS DO DIAGNÓSTICO (OK / ATENCAO / FALTA / NAO_APLICAVEL)
// ============================================================
const labelStatus = (status) => {
  const map = {
    OK: 'OK',
    ATENCAO: 'Atenção',
    FALTA: 'Pendência',
    NAO_APLICAVEL: 'N/A'
  }
  return map[status] || status
}

const classeBadgeStatus = (status) => {
  const map = {
    OK: 'bg-success',
    ATENCAO: 'bg-warning text-dark',
    FALTA: 'bg-danger',
    NAO_APLICAVEL: 'bg-secondary'
  }
  return map[status] || 'bg-secondary'
}

const classeBordaCategoria = (status) => {
  const map = {
    OK: 'border-success-subtle',
    ATENCAO: 'border-warning-subtle',
    FALTA: 'border-danger-subtle',
    NAO_APLICAVEL: 'border-secondary-subtle'
  }
  return map[status] || 'border-secondary-subtle'
}

const categoriaAberta = (index) => !!categoriasAbertas.value[index]

const toggleCategoria = (index) => {
  categoriasAbertas.value[index] = !categoriasAbertas.value[index]
}

// ============================================================
// COMPUTED: TIPO DE MOVIMENTO
// ============================================================
const isEntrada = computed(() => {
  return formData.lstTipoMovimento === TP_MOVIMENTO.Entrada
})

// ============================================================
// COMPUTED: RESUMO DA SIMULAÇÃO (origem → destino)
// ============================================================
// Entrada (compra):  origem = UF do fornecedor, destino = UF da filial
// Saída   (venda):   origem = UF da filial,   destino = UF do parceiro

const ufOrigemResumo = computed(() => {
  if (!formData.idFilial || !formData.idParceiro) return '—'
  if (isEntrada.value) {
    return ufParaSigla(parceiroInfo.value?.ufDestino) || '—'
  }
  return ufParaSigla(filialSelecionada.value?.uf) || '—'
})

const ufDestinoResumo = computed(() => {
  if (!formData.idFilial || !formData.idParceiro) return '—'
  if (isEntrada.value) {
    return ufParaSigla(filialSelecionada.value?.uf) || '—'
  }
  return ufParaSigla(parceiroInfo.value?.ufDestino) || '—'
})

const nomeOrigemResumo = computed(() => {
  if (!formData.idFilial || !formData.idParceiro) return ''
  if (isEntrada.value) {
    return parceiroSelecionado.value?.nomeFantasia || 'Fornecedor'
  }
  return filialSelecionada.value?.cidade || 'Filial'
})

const nomeDestinoResumo = computed(() => {
  if (!formData.idFilial || !formData.idParceiro) return ''
  if (isEntrada.value) {
    return filialSelecionada.value?.cidade || 'Filial'
  }
  return parceiroSelecionado.value?.nomeFantasia || 'Destino'
})

const operacaoDescricao = computed(() => {
  return operacaoSelecionada.value?.descricao || '—'
})

const operacaoTipoMovimento = computed(() => {
  const opMov = operacaoSelecionada.value?.lstTipoMovimento
  if (!opMov) return ''
  if (opMov === TP_MOVIMENTO.Entrada) return 'Entrada'
  if (opMov === TP_MOVIMENTO.Saida) return 'Saída'
  return opMov
})

const parceiroInfo = computed(() => {
  if (!parceiroSelecionado.value) return null
  const p = parceiroSelecionado.value
  // Tipo de pessoa: resolve o texto completo via lista PARCEIRO_TIPO
  const tipoPessoa = getTextoLista('LstTipoPessoa', p.lstTipoPessoa) || p.lstTipoPessoa || '—'
  // UF: busca do endereço de faturamento (idEnderecoFornecedorFaturamento)
  let ufBruta = null
  if (p.enderecoFaturamento?.uf) {
    ufBruta = p.enderecoFaturamento.uf
  } else if (p.idEnderecoFornecedorFaturamento && p.enderecos) {
    const endFaturamento = p.enderecos.find(
      e => e.idParceiroEndereco === p.idEnderecoFornecedorFaturamento
    )
    ufBruta = endFaturamento?.uf || null
  } else {
    ufBruta = p.uf || null
  }
  // Regime tributário: resolve o texto completo via lista REGIME_TRIBUTARIO
  const regime = getTextoLista('LstRegimeTributario', p.lstRegimeTributario) || p.lstRegimeTributario || null
  return {
    tipoPessoa,
    ufDestino: ufParaSigla(ufBruta) || '—',
    regimeTributario: regime,
    isOrgaoGoverno: !!p.isOrgaoGoverno
  }
})

const produtoInfo = computed(() => {
  if (!produtoSelecionado.value) return null
  const p = produtoSelecionado.value
  const isServico = p.lstNaturezaItem === PROD_NATUREZA.Servico
  // NBS/LC116 vêm como IdListaClienteItem (ex: "FISC_NBS4") — resolve para TextoLista (ex: "10.11")
  const nbsBruto = p.lstCodigoNbs || p.codigoNbs
  const lc116Bruto = p.lstCodigoLc116 || p.codigoLc116
  const nbs = getTextoLista('LstCodigoNbs', nbsBruto) || nbsBruto
  const lc116 = getTextoLista('LstCodigoLc116', lc116Bruto) || lc116Bruto
  return {
    codigoFiscal: isServico ? (nbs || lc116 || '—') : (p.codigoNcm || '—'),
    naturezaLabel: isServico ? 'Serviço' : 'Produto Físico',
    isServico
  }
})

// ============================================================
// CONFIGS DE BUSCA
// ============================================================
const configBuscaOperacao = criarConfigBuscaOperacao(authStore.organizacaoAtiva)
const configBuscaProduto = criarConfigBuscaProduto()

const configBuscaParceiro = parceiroSearch

// ============================================================
// OPTIONS PARA SELECTS
// ============================================================
const tipoMovimentoOptions = [
  { idListaClienteItem: TP_MOVIMENTO.Entrada, textoLista: 'Entrada' },
  { idListaClienteItem: TP_MOVIMENTO.Saida, textoLista: 'Saída' }
]

// ============================================================
// PODE SIMULAR?
// ============================================================
const podeSimular = computed(() => {
  return (
    formData.idFilial &&
    formData.idOperacao &&
    formData.idParceiro &&
    formData.idProduto &&
    formData.valorBase > 0 &&
    formData.lstTipoMovimento &&
    formData.lstNivelModuloFiscal
  )
})

// ============================================================
// HANDLERS
// ============================================================
const handleFilialSelecionado = (filial) => {
  if (!filial) return
  filialSelecionada.value = filial
  formData.idFilial = filial.idFilial
}

const handleOperacaoSelecionado = (operacao) => {
  if (!operacao) return
  operacaoSelecionada.value = operacao
  formData.idOperacao = operacao.idOperacao
}

const handleParceiroSelecionado = async (parceiro) => {
  if (!parceiro) return
  try {
    // 1. Busca o parceiro completo da API (já inclui enderecos[] com idParceiroEndereco e uf)
    const res = await api.get(`/Parceiros/${parceiro.idParceiro}`)
    const parceiroCompleto = res.data

    // 2. Resolve o endereço de faturamento localmente no array enderecos[] já carregado
    const idEndFaturamento = parceiroCompleto.idEnderecoFornecedorFaturamento
    if (idEndFaturamento && parceiroCompleto.enderecos?.length) {
      const endereco = parceiroCompleto.enderecos.find(
        e => e.idParceiroEndereco === idEndFaturamento
      )
      if (endereco) {
        parceiroCompleto.enderecoFaturamento = endereco
      }
    }

    parceiroSelecionado.value = parceiroCompleto
  } catch {
    // Fallback: usa o objeto da busca
    parceiroSelecionado.value = parceiro
  }
  formData.idParceiro = parceiro.idParceiro
}

const handleProdutoSelecionado = async (produto) => {
  if (!produto) return
  try {
    // Busca o produto completo da API para obter NBS, LC116
    const res = await api.get(`/Produtos/${produto.idProduto}`)
    // Merge: API tem LstCodigoNbs/LstCodigoLc116, mas search tem lstNaturezaItem
    produtoSelecionado.value = { ...res.data, ...produto }
  } catch {
    // Fallback: usa o objeto parcial da busca
    produtoSelecionado.value = produto
  }
  formData.idProduto = produto.idProduto
}

// ============================================================
// SIMULAR
// ============================================================
const handleSimular = async () => {
  if (!podeSimular.value) return

  loading.value = true
  erro.value = null
  resultado.value = null
  exibirJson.value = false

  try {
    const _p = parceiroSelecionado.value
    const prod = produtoSelecionado.value
    const isServico = prod.lstNaturezaItem === PROD_NATUREZA.Servico

    // Monta o payload para o endpoint de simulação
    // Entrada: origem = UF do fornecedor, destino = UF da filial
    // Saída:   origem = UF da filial,   destino = UF do parceiro
    // As UFs são normalizadas para código interno (ex: "GEO_UF8") via ufParaCodigo
    // Resolve as UFs para envio normalizadas para código interno
    // Para entrada, a origem é a UF do endereço de faturamento do fornecedor
    // Para saída, a origem é a UF da filial
    const ufOrigemSigla = isEntrada.value
      ? (parceiroInfo.value?.ufDestino || 'EX')
      : ufParaSigla(filialSelecionada.value?.uf) || 'EX'

    const ufDestinoSigla = isEntrada.value
      ? ufParaSigla(filialSelecionada.value?.uf) || 'EX'
      : (parceiroInfo.value?.ufDestino || 'EX')

    const ufOrigemParaEnvio = ufParaCodigo(ufOrigemSigla)
    const ufDestinoParaEnvio = ufParaCodigo(ufDestinoSigla)

    const payload = {
      idOperacao: formData.idOperacao,
      lstUfOrigem: ufOrigemParaEnvio,
      lstUfDestino: ufDestinoParaEnvio,
      lstTipoMovimento: formData.lstTipoMovimento,
      isServico,
      codigoNcm: isServico ? null : (prod.codigoNcm || null),
      // NBS/LC116 vêm como IdListaClienteItem — resolve para TextoLista
      codigoNbs: isServico
        ? (getTextoLista('LstCodigoNbs', prod.lstCodigoNbs) || prod.lstCodigoNbs || prod.codigoNbs || null)
        : null,
      codigoLc116: isServico
        ? (getTextoLista('LstCodigoLc116', prod.lstCodigoLc116) || prod.lstCodigoLc116 || prod.codigoLc116 || null)
        : null,
      lstNaturezaItem: prod.lstNaturezaItem || null,
      valorBase: formData.valorBase,
      // IMPORTANTE: Envia o código original (ex: "PARCEIRO_TIPO3"), NÃO o texto resolvido (ex: "Estrangeiro")
      tipoPessoaParceiro: _p?.lstTipoPessoa || null,
      regimeTributarioParceiro: _p?.lstRegimeTributario || null,
      // GUARDIÃO: Indica se o parceiro é órgão de governo (IsOrgaoGoverno=true)
      isOrgaoGovernoParceiro: !!_p?.isOrgaoGoverno,
      // Código IBGE do município de destino (filial) para resolução da Prefeitura (ISS)
      codigoIbge: filialSelecionada.value?.codigoMunicipioIbge || null,
      lstNivelModuloFiscalOverride: formData.lstNivelModuloFiscal,
      // Bloco de categorias financeiras simuladas
      idProduto: formData.idProduto,
      valorDesconto: formData.valorDesconto || 0
    }

    const res = await api.post('/Fiscal/simular', payload)
    resultado.value = res.data
  } catch (e) {
    const data = e.response?.data || {}
    const msg = data.erro || e.message || 'Erro inesperado ao simular cálculo fiscal.'
    erro.value = msg
    relatorioVerificacoes.value = data.relatorioVerificacoes || null
    exibirRelatorioVerificacoes.value = !!relatorioVerificacoes.value
    resultado.value = {
      sucesso: false,
      mensagemDebug: msg
    }
  } finally {
    loading.value = false
  }
}

// ============================================================
// REINICIAR
// ============================================================
const reiniciarSimulacao = () => {
  // Preserva a filial
  const filialId = formData.idFilial
  const filialObj = filialSelecionada.value

  // Limpa tudo
  formData.idOperacao = null
  formData.idParceiro = null
  formData.idProduto = null
  formData.valorBase = null
  formData.valorDesconto = null
  formData.lstTipoMovimento = TP_MOVIMENTO.Entrada
  // Restaura o nível fiscal da empresa (ou Advanced como fallback)
  const nivelDaEmpresa = authStore.lstNivelModuloFiscal
  formData.lstNivelModuloFiscal = (nivelDaEmpresa && nivelDaEmpresa !== NV_MODULO_FISCAL.Nenhum)
    ? nivelDaEmpresa
    : NV_MODULO_FISCAL.Advanced

  operacaoSelecionada.value = null
  parceiroSelecionado.value = null
  produtoSelecionado.value = null
  resultado.value = null
  erro.value = null
  exibirJson.value = false
  exibirRelatorioVerificacoes.value = false
  relatorioVerificacoes.value = null

  // Restaura a filial
  formData.idFilial = filialId
  filialSelecionada.value = filialObj
}

// ============================================================
// DIAGNOSTICAR REGRAS
// ============================================================
const handleDiagnosticarRegras = async () => {
  if (!podeSimular.value) return

  diagnosticando.value = true
  diagnostico.value = null
  erro.value = null
  categoriasAbertas.value = {}

  try {
    const ufOrigemSigla = isEntrada.value
      ? (parceiroInfo.value?.ufDestino || 'EX')
      : ufParaSigla(filialSelecionada.value?.uf) || 'EX'

    const ufDestinoSigla = isEntrada.value
      ? ufParaSigla(filialSelecionada.value?.uf) || 'EX'
      : (parceiroInfo.value?.ufDestino || 'EX')

    // O backend busca os cadastros pelos IDs (filial/operação/parceiro/produto)
    // e devolve a prontidão por categoria: o que está OK e o que falta fazer.
    const payload = {
      idFilial: formData.idFilial,
      idOperacao: formData.idOperacao,
      idParceiro: formData.idParceiro,
      idProduto: formData.idProduto,
      lstUfOrigem: ufParaCodigo(ufOrigemSigla),
      lstUfDestino: ufParaCodigo(ufDestinoSigla),
      lstTipoMovimento: formData.lstTipoMovimento,
      lstNivelModuloFiscalOverride: formData.lstNivelModuloFiscal
    }

    const res = await api.post('/Fiscal/diagnosticar-parametros', payload)
    diagnostico.value = res.data
  } catch (e) {
    const data = e.response?.data || {}
    const msg = data.erro || data.message || e.message || 'Erro inesperado ao diagnosticar os parâmetros fiscais.'
    erro.value = msg
  } finally {
    diagnosticando.value = false
  }
}

const limparDiagnostico = () => {
  diagnostico.value = null
  categoriasAbertas.value = {}
  exibirJsonDiagnostico.value = false
}

const copiarMemoriaCalculo = () => {
  if (!resultado.value?.memoriaCalculoJson) return
  navigator.clipboard.writeText(resultado.value.memoriaCalculoJson).then(() => {
    copiado.value = true
    setTimeout(() => { copiado.value = false }, 2000)
  }).catch(() => {
    // Fallback para navegadores sem clipboard API
    const textarea = document.createElement('textarea')
    textarea.value = resultado.value.memoriaCalculoJson
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    copiado.value = true
    setTimeout(() => { copiado.value = false }, 2000)
  })
}

const copiarDiagnostico = () => {
  if (!diagnostico.value) return
  const texto = JSON.stringify(diagnostico.value, null, 2)
  navigator.clipboard.writeText(texto).then(() => {
    copiadoDiagnostico.value = true
    setTimeout(() => { copiadoDiagnostico.value = false }, 2000)
  }).catch(() => {
    const textarea = document.createElement('textarea')
    textarea.value = texto
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    copiadoDiagnostico.value = true
    setTimeout(() => { copiadoDiagnostico.value = false }, 2000)
  })
}

const copiarResultado = () => {
  if (!resultado.value) return
  const texto = JSON.stringify(resultado.value, null, 2)
  navigator.clipboard.writeText(texto).then(() => {
    copiadoResultado.value = true
    setTimeout(() => { copiadoResultado.value = false }, 2000)
  }).catch(() => {
    const textarea = document.createElement('textarea')
    textarea.value = texto
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    copiadoResultado.value = true
    setTimeout(() => { copiadoResultado.value = false }, 2000)
  })
}

// ============================================================
// UTILITÁRIOS
// ============================================================
const formatarMoeda = (valor) => {
  if (valor == null || isNaN(valor)) return 'R$ 0,00'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valor)
}

const badgeEsfera = (esfera) => {
  const map = {
    Fornecedor: 'bg-primary',
    Cliente: 'bg-success',
    Federal: 'bg-danger',
    Estadual: 'bg-warning text-dark',
    Municipal: 'bg-info text-dark'
  }
  return map[esfera] || 'bg-secondary'
}

// ============================================================
// HELPERS DO BLOCO DE TÍTULOS SIMULADOS
// ============================================================

/// <summary>
/// Retorna a classe CSS do badge para o tipo de exibição da categoria.
/// </summary>
const classeBadgeTipoExibicao = (tipo) => {
  const map = {
    BASE: 'bg-primary',
    DESCONTO: 'bg-warning text-dark',
    IMPOSTO: 'bg-danger',
    IMPOSTO_RECUPERAVEL: 'bg-info text-dark'
  }
  return map[tipo] || 'bg-secondary'
}

const labelTipoExibicao = (tipo, isCompra) => {
  const map = {
    BASE: isCompra ? 'CPV' : 'Receita',
    DESCONTO: 'Desconto',
    IMPOSTO: 'Imposto',
    IMPOSTO_RECUPERAVEL: 'Imp. Recuperável'
  }
  return map[tipo] || tipo
}
</script>

<style scoped>
.simulador-fiscal-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--bs-body-bg);
}

.simulador-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* ============================================================ */
/* CABEÇALHO                                                     */
/* ============================================================ */
.simulador-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background-color: #34495E;
  border-bottom: 1px solid #1f2d3a;
  flex-shrink: 0;
}

[data-bs-theme="dark"] .simulador-header {
  background-color: var(--bs-dark-bg-subtle);
  border-bottom: 1px solid var(--bs-border-color);
}

.titulo-wrapper {
  display: flex;
  flex-direction: column;
}

.titulo-principal {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #FFFFFF;
}

[data-bs-theme="dark"] .titulo-principal {
  color: var(--bs-body-color);
}

.titulo-sub {
  font-size: 0.8rem;
  color: #ADB5BD;
}

[data-bs-theme="dark"] .titulo-sub {
  color: var(--bs-secondary-color);
}

.botoes-wrapper {
  margin-left: auto;
}

.botoes-wrapper .btn {
  color: #fff;
  border-color: rgba(255,255,255,0.3);
}

[data-bs-theme="dark"] .botoes-wrapper .btn {
  color: var(--bs-body-color);
  border-color: var(--bs-border-color);
}

.botoes-wrapper .btn:hover {
  background-color: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.5);
}

[data-bs-theme="dark"] .botoes-wrapper .btn:hover {
  background-color: var(--bs-tertiary-bg);
  border-color: var(--bs-border-color);
}

/* ============================================================ */
/* CARDS                                                         */
/* ============================================================ */
.card {
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.03);
}

.card-header {
  padding: 0.6rem 1rem;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.card-body {
  padding: 1rem;
}

/* ============================================================ */
/* JSON PRE                                                      */
/* ============================================================ */
.json-pre {
  background-color: #1e1e1e;
  color: #d4d4d4;
  padding: 1rem;
  font-size: 0.75rem;
  line-height: 1.4;
  max-height: 400px;
  overflow: auto;
  border-radius: 0 0 0.375rem 0.375rem;
}

[data-bs-theme="dark"] .json-pre {
  background-color: var(--bs-dark-bg-subtle);
  color: var(--bs-body-color);
}

.cursor-pointer {
  cursor: pointer;
}

/* ============================================================ */
/* RESPONSIVIDADE                                                */
/* ============================================================ */
@media (max-width: 991.98px) {
  .simulador-header {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .botoes-wrapper {
    margin-left: 0;
  }
}
</style>
